import {
  AuthorizationType,
  DigiLaGetMinorUserInfoResponse,
  DigiLaUserInfoResponse,
  MinorServiceType,
} from '@shared/digila'
import { PhoneNumberWithPhoneCountry } from '@shared/types'
import { KycFinishedHandlerInput } from '@ui-common/kyc/KycWizard'
import { CommonFormStepLogic, CommonWizardLogic, YesNo } from '@ui-common/types'

import { AoMembershipFormData } from './components/ServiceWizard/AoMembership/types'
import { BankAccountBasicsFormData } from './components/ServiceWizard/BankAccountService/BankAccountBasicsStep/types'
import { BankAccountDetailsFormData } from './components/ServiceWizard/BankAccountService/BankAccountDetailsStep/types'
import { SERVICE_WIZARD_STATE } from './state/wizardMachine'

export type MinorKycDoneHandler = (kycFinishedHandlerInput: KycFinishedHandlerInput) => void

export interface DigilaContext
  extends CommonStateMachineContext,
    AvailableServicesData,
    AvailableAuthorizationsData,
    WizardActions<StepType | typeof SERVICE_WIZARD_STATE> {
  minorUser: MinorUser | null
  user: User
  result?: ResultType
  onWizardDone: (wizardData: WizardData) => void
  onApplicationRejected: () => void
  onMinorKycDone: MinorKycDoneHandler
  onReset: () => void
}

export interface AvailableServicesData {
  availableServices: MinorServiceType[]
}
export interface AvailableAuthorizationsData {
  availableAuthorizations: AuthorizationType[]
}

export type WizardActions<TStep> = CommonWizardLogic<TStep, WizardData, EditableStep>

export interface CommonStateMachineContext {
  isEditing?: boolean
  wizardData: WizardData
}

export enum StepType {
  Initialize = 'initialize',
  Entrance = 'entrance',
  Authenticate = 'authenticate',
  GetUserInfo = 'get-user-info',
  MinorSsn = 'minor-ssn',
  DigipaRequiredGate = 'digipa-required-gate',
  DigiliRequiredGate = 'digili-required-gate',
  KycGate = 'kyc-gate',
  GuardianKyc = 'guardian-kyc',
  ServiceCatalog = 'service-catalog',
  SecondGuardianStep = 'second-guardian-step',
  MinorAoMembership = 'minor-ao-membership',
  MinorKYC = 'minor-kyc',
  BankAccountBasics = 'bank-account-basics',
  BankAccountDetails = 'bank-account-details',
  InvestmentServicesDetails = 'investment-services-details',
  Authorization = 'authorization-details',
  Status = 'status',
  SendStep = 'send',
  AcceptStep = 'accept',
  RejectStep = 'reject',
  End = 'end',
}

export const PhaseSteps = [
  StepType.Status,
  StepType.MinorKYC,
  StepType.BankAccountDetails,
  StepType.BankAccountBasics,
  StepType.MinorAoMembership,
  StepType.InvestmentServicesDetails,
  StepType.SecondGuardianStep,
  StepType.Authorization,
  StepType.SendStep,
  StepType.AcceptStep,
  StepType.RejectStep,
  StepType.ServiceCatalog,
]

export type EditableStep =
  | StepType.BankAccountBasics
  | StepType.BankAccountDetails
  | StepType.InvestmentServicesDetails
  | StepType.Authorization
  | StepType.SecondGuardianStep

export type BankAccountServiceDetailsData = {
  bankAccountTypeData: BankAccountBasicsFormData
  bankAccountDetails?: BankAccountDetailsFormData | null
}

export type BankAccountServiceData = {
  bankAccountData?: BankAccountServiceDetailsData
}

export type InvestmentServiceDetailsData =
  | {
      useCreatedAccount: YesNo.Yes
      bankAccount?: undefined
    }
  | {
      useCreatedAccount: YesNo.No
      bankAccount: string
    }
  | {
      useCreatedAccount: undefined
      bankAccount: string
    }

export type InvestmentServiceData = {
  investmentServiceData?: InvestmentServiceDetailsData
}

export type AoMembershipData = {
  aoMembershipData?: AoMembershipFormData
}

export type MinorData = {
  minorUser?: MinorUser
}

export interface WizardData extends BankAccountServiceData, InvestmentServiceData, AoMembershipData {
  selectedServices?: MinorServiceType[]
  selectedAuthorizations?: AuthorizationType[]
  secondGuardianPhoneNumber?: PhoneNumberWithPhoneCountry
  isAuthorizationComplete?: boolean
}

export interface User extends DigiLaUserInfoResponse, Guardian {}

export type MinorUser = DigiLaGetMinorUserInfoResponse

export interface Guardian {
  hasNetbank: boolean
}

export interface Phases {
  minorKyc: {
    shouldBeUpdated: boolean
    isComplete: boolean
  }
  aoMembership: {
    shouldBeUpdated: boolean
    isComplete: boolean
  }
  applyAuthorization?: {
    authorizations: AuthorizationType[]
    isComplete: boolean
  }
  services: {
    type: MinorServiceType
    isComplete: boolean
  }[]
}

export enum PreparationPhase {
  MinorKyc = 'minor-kyc',
  AoMembership = 'ao-membership',
  SecondGuardianPhoneNumber = 'second-guardian-phone-number',
}

export interface PhaseCompletionStatus {
  phaseType: PhaseType
  isComplete: boolean
}

export type PhaseType = PreparationPhase | MinorServiceType | 'authorization'

export type Ssn = string

export enum ResultType {
  ApplicationPending = 'application-in-progress',
  ApplicationWaitingForApproval = 'application-waiting-approval',
  ApplicationSent = 'application-sent',
  ApplicationRejected = 'application-rejected',
  GuardianKycInvalid = 'guardian-kyc-invalid',
  MinorKycInvalid = 'minor-kyc-invalid',
}

export interface DigilaStepSummaryData {
  minorUser: MinorUser
  editData?: (dataType: EditableStep) => void
  wizardData: WizardData
}

export type BankAccountSummaryData = DigilaStepSummaryData & {
  shouldShowSecondGuardianPaymentLimit: boolean
}

export type ServiceStepLogic<TStepData> = CommonFormStepLogic<WizardData, TStepData> & { onBackToSummary: () => void }

export interface CatalogSelections extends AvailableAuthorizationsData, AvailableServicesData {
  onServicesSelected: (selectedServices: MinorServiceType[], selectedAuthorizations: AuthorizationType[]) => void
}
